/**
 * Gallery Page - Cake Portfolio Management
 * 
 * Tammy's showcase of completed cakes and baked goods.
 * Customers can browse for inspiration before ordering.
 */

import { useState, useEffect } from 'react';
import { 
  HiPhotograph, 
  HiPlus, 
  HiTrash, 
  HiStar,
  HiRefresh,
  HiPencil,
  HiX,
  HiCheck
} from 'react-icons/hi';
import ImageSelector from '../components/ImageSelector';
import { adminFetch, adminHeaders } from '../lib/adminApi';

interface GalleryImage {
  id: string;
  title: string | null;
  description: string | null;
  image_url: string;
  category: string;
  is_featured: boolean;
  created_at: string;
}

interface ImageForm {
  title: string;
  description: string;
  image_url: string;
  category: string;
  is_featured: boolean;
}

const emptyForm: ImageForm = {
  title: '',
  description: '',
  image_url: '',
  category: 'Custom Cakes',
  is_featured: false,
};

const CATEGORIES = [
  'Birthday Cakes',
  'Wedding Cakes',
  'Baby Shower',
  'Custom Cakes',
  'Cupcakes',
  'Cookies',
  'Holiday',
  'Other',
];

export default function Gallery() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ImageForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [describing, setDescribing] = useState(false);
  const [describeError, setDescribeError] = useState<string | null>(null);

  // Auto-run OpenRouter describe when adding a new image (pre-fill title/description; Tammy can edit)
  useEffect(() => {
    if (!showModal || editingId || !form.image_url.trim()) {
      setDescribeError(null);
      return;
    }
    const imageUrl = form.image_url.trim();
    let cancelled = false;
    setDescribing(true);
    setDescribeError(null);
    adminFetch('/api/gallery/describe-image', {
      method: 'POST',
      headers: adminHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ image_url: imageUrl }),
    })
      .then((res) => {
        if (cancelled) return res.json().catch(() => ({}));
        if (!res.ok) {
          return res.json().catch(() => ({ error: 'Could not get description' })).then((data) => {
            throw new Error(data.details || data.error || 'Describe failed');
          });
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setForm((prev) => ({
          ...prev,
          title: data.title || prev.title,
          description: data.description || prev.description,
          category: data.suggested_category && CATEGORIES.includes(data.suggested_category)
            ? data.suggested_category
            : prev.category,
        }));
      })
      .catch((err) => {
        if (!cancelled) {
          setDescribeError(err instanceof Error ? err.message : 'Could not suggest description');
        }
      })
      .finally(() => {
        if (!cancelled) setDescribing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showModal, editingId, form.image_url]);

  // Fetch gallery images
  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await adminFetch('/api/gallery');
      if (!response.ok) throw new Error('Failed to fetch gallery');
      const data = await response.json();
      setImages(data.images || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  // Filter images
  const filteredImages = selectedCategory === 'all' 
    ? images 
    : images.filter(img => img.category === selectedCategory);

  // Open modal for new image
  const openNewModal = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  };

  // Open modal for editing
  const openEditModal = (image: GalleryImage) => {
    setForm({
      title: image.title || '',
      description: image.description || '',
      image_url: image.image_url,
      category: image.category,
      is_featured: image.is_featured,
    });
    setEditingId(image.id);
    setShowModal(true);
  };

  // Save image
  const handleSave = async () => {
    if (!form.image_url.trim()) {
      alert('Image URL is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingId ? `/api/gallery/${editingId}` : '/api/gallery';
      const method = editingId ? 'PUT' : 'POST';

      const response = await adminFetch(url, {
        method,
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          title: form.title.trim() || null,
          description: form.description.trim() || null,
          image_url: form.image_url.trim(),
          category: form.category,
          is_featured: form.is_featured,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save');
      }

      setShowModal(false);
      fetchImages();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save image';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    } finally {
      setSaving(false);
    }
  };

  // Toggle featured
  const toggleFeatured = async (id: string) => {
    try {
      const response = await adminFetch(`/api/gallery/${id}/toggle-featured`, {
        method: 'PUT',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update');
      }
      
      setImages(images.map(img => 
        img.id === id ? { ...img, is_featured: !img.is_featured } : img
      ));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update gallery';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Delete image
  const deleteImage = async (id: string) => {
    if (!confirm('Delete this photo from gallery?')) return;
    
    try {
      const response = await adminFetch(`/api/gallery/${id}`, { method: 'DELETE' });
      
      if (!response.ok) {
        let errorMessage = 'Failed to delete image';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      // Success - refresh the gallery list from server
      await fetchImages();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete image';
      alert(`Error: ${message}`);
      console.error('Delete error:', err);
    }
  };

  // Get unique categories from images
  const usedCategories = ['all', ...new Set(images.map(img => img.category))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cake Portfolio</h1>
          <p className="text-gray-500 mt-1">
            Showcase your best work • {images.length} photos
          </p>
          <p className="text-gray-500 mt-1 text-sm">
            To see all images or delete files from storage, use <strong>All Images</strong> in the sidebar.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchImages}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Refresh"
          >
            <HiRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openNewModal}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white 
                       rounded-lg hover:bg-purple-700 transition-colors"
          >
            <HiPlus className="w-5 h-5" />
            Add Photo
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {usedCategories.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors
                       ${selectedCategory === cat 
                         ? 'bg-purple-600 text-white' 
                         : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {cat === 'all' ? 'All Photos' : cat}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading gallery...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && images.length === 0 && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <HiPhotograph className="w-16 h-16 text-purple-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">No Photos Yet</h3>
          <p className="text-gray-500 mt-2 mb-4">
            Add photos of your beautiful cakes to inspire customers!
          </p>
          <button
            onClick={openNewModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 
                       text-white rounded-lg hover:bg-purple-700"
          >
            <HiPlus className="w-5 h-5" />
            Add First Photo
          </button>
        </div>
      )}

      {/* Gallery Grid */}
      {!loading && filteredImages.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map(image => (
            <div 
              key={image.id}
              className="relative group bg-gray-100 rounded-xl aspect-square overflow-hidden"
            >
              {/* Image */}
              <img
                src={image.image_url}
                alt={image.title || 'Gallery image'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/assets/images/branding/logo2.webp';
                }}
              />

              {/* Featured badge */}
              {image.is_featured && (
                <div className="absolute top-2 left-2 px-2 py-1 bg-amber-400 text-amber-900 
                              rounded-full text-xs font-medium flex items-center gap-1">
                  <HiStar className="w-3 h-3" />
                  Featured
                </div>
              )}

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 
                            transition-opacity flex items-center justify-center gap-2">
                <button
                  onClick={() => toggleFeatured(image.id)}
                  className={`p-2 rounded-full ${image.is_featured 
                    ? 'bg-amber-400 text-amber-900' 
                    : 'bg-white text-gray-700'}`}
                  title={image.is_featured ? 'Remove from featured' : 'Add to featured'}
                >
                  <HiStar className="w-5 h-5" />
                </button>
                <button
                  onClick={() => openEditModal(image)}
                  className="p-2 bg-white text-gray-700 rounded-full"
                  title="Edit"
                >
                  <HiPencil className="w-5 h-5" />
                </button>
                <button
                  onClick={() => deleteImage(image.id)}
                  className="p-2 bg-red-500 text-white rounded-full"
                  title="Delete"
                >
                  <HiTrash className="w-5 h-5" />
                </button>
              </div>

              {/* Title & Category */}
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                <p className="text-white text-sm font-medium truncate">
                  {image.title || 'Untitled'}
                </p>
                <p className="text-white/70 text-xs">{image.category}</p>
              </div>
            </div>
          ))}

          {/* Upload placeholder */}
          <button 
            onClick={openNewModal}
            className="bg-gray-50 rounded-xl aspect-square border-2 border-dashed border-gray-300 
                       flex flex-col items-center justify-center gap-2 hover:border-purple-400 
                       hover:bg-purple-50 transition-colors"
          >
            <HiPlus className="w-8 h-8 text-gray-400" />
            <span className="text-sm text-gray-500">Add Photo</span>
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-purple-50 rounded-xl p-4">
          <p className="text-2xl font-bold text-purple-600">{images.length}</p>
          <p className="text-sm text-gray-600">Total Photos</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-600">
            {images.filter(i => i.is_featured).length}
          </p>
          <p className="text-sm text-gray-600">Featured Photos</p>
        </div>
      </div>

      {/* Help */}
      <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
        <p className="font-medium">💡 Portfolio Tips:</p>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li>Featured photos appear on your homepage & gallery page</li>
          <li>Use high-quality photos showing your best work</li>
          <li>Add titles like "6-inch Chocolate Birthday Cake" for context</li>
          <li>Photos help customers see what to expect!</li>
        </ul>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingId ? 'Edit Photo' : 'Add Photo'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Close modal"
                aria-label="Close modal"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Image Upload - Using ImageSelector component */}
              <ImageSelector
                value={form.image_url}
                onChange={(url) => setForm(prev => ({ ...prev, image_url: url }))}
                label="Photo *"
                helpText="Browse existing images, upload from your device, or paste a URL"
                previewHeight="h-48"
              />
              {describing && (
                <p className="text-sm text-purple-600 flex items-center gap-2" aria-live="polite">
                  <span className="inline-block w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                  Getting description…
                </p>
              )}
              {describeError && (
                <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2" role="alert">
                  {describeError} You can still add a title and description below.
                </p>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., 6-inch Chocolate Birthday Cake"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  placeholder="Add details about this cake..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  aria-label="Select category"
                  title="Select category"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Featured */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_featured}
                  onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700">Feature this photo (show on homepage)</span>
              </label>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-4 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg 
                           hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? 'Saving...' : (
                  <>
                    <HiCheck className="w-5 h-5" />
                    {editingId ? 'Update' : 'Add Photo'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
